import React, { useCallback, useContext } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { SkillBiteEditSlice } from '../../../../../reducers'
import { SectionIcon, SessionIcon } from '../icons'
import AddSessionDialog from '../add-session-dialog'
import EditSessionDialog from '../edit-session-dialog'
import AddSectionDialog from '../add-section-dialog'
import RenameSectionDialog from '../rename-section-dialog'
import DeleteSection from '../delete-section'
import DeleteSession from '../delete-session'
import { TooltipOverflowTitle } from './components/tooltip-overflow-title'
import { useStyles } from './styles'
import { BuildNavigationContext } from '../..'

const SessionSelector = ({ sessions }) => {
  const canEditStructure = useSelector(SkillBiteEditSlice.selectors.selectCanEditStructure)
  const canEdit = useSelector(SkillBiteEditSlice.selectors.selectEditMode)
  const classes = useStyles({ canEditStructure })
  const selectedSession = useSelector(SkillBiteEditSlice.selectors.selectSelectedSession)
  const selectedSection = useSelector(SkillBiteEditSlice.selectors.selectSelectedSection)
  const selectedPage = useSelector(SkillBiteEditSlice.selectors.selectSelectedPage)

  const { changeSelectedSession, changeSelectedSection, changeSelectedPage } = useContext(BuildNavigationContext)

  const handleChangeSelectedSession = useCallback((session) => {
    if (selectedSession?.id === session?.id) return
    changeSelectedSession(session)
  }, [changeSelectedSession, selectedSession])

  const handleChangeSelectedSection = useCallback((section) => {
    if (selectedSection?.id === section?.id) return
    changeSelectedSection(section)
  }, [changeSelectedSection, selectedSection])

  const handleChangeSelectedPage = useCallback((page) => {
    changeSelectedPage(page)
  }, [changeSelectedPage])

  return (
    <Box>
      <Box className={classes.sessionListContainer}>
        {sessions.map((session, index) => {
          const isSelected = session.id === selectedSession?.id

          return (
            <Accordion
              sx={{
                border: `1px solid ${isSelected ? '#4AA5EF' : '#BCBCBC'}`
              }}
              className={classes.accordion}
              square={false}
              key={index}
              expanded={isSelected}
              onChange={() => handleChangeSelectedSession(session)}
            >
              <AccordionSummary>
                <Box
                  display='flex'
                  flexDirection='row'
                  alignItems='center'
                  className={[classes.sessionItem, isSelected ? 'selected' : ''].join(' ')}
                >
                  <SessionIcon />
                  <TooltipOverflowTitle
                    title={session.name}
                    minCharTooltip={30}
                    typographySx={{ fontWeight: isSelected ? 600 : 400, flex: 1 }}
                  />
                  {canEdit && (
                    <EditSessionDialog session={session} className={classes.editSessionButton} />
                  )}
                  {canEditStructure && (
                    <DeleteSession
                      session={session}
                      className={classes.deleteSessionButton}
                      loadingClassName={classes.deleteSessionLoadingButton}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Sections</Typography>
                  <Divider />
                  <Box display='flex' flexDirection='column' gap={1} pt={1}>
                    {session.sections.map((section, sectionIndex) => {
                      const isSectionSelected = section.id === selectedSection?.id
                      return (
                        <>
                          <Box
                            key={sectionIndex}
                            display='flex'
                            flexDirection='row'
                            alignItems='center'
                            className={classes.sectionItem}
                            onClick={() => handleChangeSelectedSection(section)}
                          >
                            <SectionIcon />
                            <TooltipOverflowTitle
                              title={section.name}
                              minCharTooltip={27}
                              typographySx={{ fontWeight: isSectionSelected ? 600 : 400, flex: 1 }}
                            />
                            {canEdit && (
                              <RenameSectionDialog
                                sessionId={session.id}
                                section={section}
                                className={classes.editSectionButton}
                              />
                            )}
                            {canEditStructure && (
                              <DeleteSection
                                sessionId={session.id}
                                className={classes.deleteSectionButton}
                                loadingClassName={classes.deleteSectionLoadingButton}
                                section={section}
                              />
                            )}
                          </Box>
                          <Box>
                            {isSectionSelected && (
                              <Box component='ul' sx={{ margin: 0 }}>
                                {section.pages.map((page, pageIndex) => {
                                  const isPageSelected = page.id === selectedPage?.id
                                  return (
                                    <Box
                                      key={pageIndex}
                                      component='li'
                                      className={classes.pageItem}
                                      sx={{ marginBottom: '8px', ':hover': { cursor: 'pointer', background: '#F2F2F2' }, '::marker': { color: isPageSelected ? '#000' : '#777' } }}
                                      onClick={() => handleChangeSelectedPage(page)}
                                    >
                                      <Typography
                                        className={classes.pageItem}
                                        sx={{ fontWeight: isPageSelected ? 600 : 400 }}
                                      >
                                        Page {pageIndex + 1}
                                      </Typography>
                                    </Box>
                                  )
                                })}
                              </Box>
                            )}
                          </Box>
                        </>
                      )
                    })}
                    {canEditStructure && (
                      <AddSectionDialog sessionId={session.id} />
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
      {canEditStructure && (
        <Box sx={{ position: 'sticky', background: '#FFF' }}>
          <AddSessionDialog />
        </Box>
      )}
    </Box>
  )
}

export default SessionSelector
